import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Renderer2,
  inject,
} from '@angular/core';

@Directive({
  selector: '[appTooltip]',
  standalone: true,
})
export class TooltipDirective implements OnDestroy {
  @Input('appTooltip') tooltipText = '';
  @Input() tooltipPlacement: string | string[] = 'auto';

  private tooltipElement: HTMLElement | null = null;
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (!this.tooltipText) {
      return;
    }
    this.showTooltip();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.hideTooltip();
  }

  @HostListener('focusin')
  onFocusIn(): void {
    if (!this.tooltipText) {
      return;
    }
    this.showTooltip();
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this.hideTooltip();
  }

  ngOnDestroy(): void {
    this.hideTooltip();
  }

  private showTooltip(): void {
    if (this.tooltipElement) {
      return;
    }

    this.tooltipElement = this.renderer.createElement('div');
    this.renderer.setAttribute(this.tooltipElement, 'role', 'tooltip');

    const classes = [
      'fixed',
      'z-[9999]',
      'px-3',
      'py-1.5',
      'text-xs',
      'font-medium',
      'text-white',
      'bg-slate-900',
      'rounded-md',
      'shadow-md',
      'pointer-events-none',
      'transition-opacity',
      'duration-200',
      'opacity-0',
      'max-w-[250px]',
      'break-words',
      'whitespace-normal',
    ];
    classes.forEach((cls) => this.renderer.addClass(this.tooltipElement!, cls));

    const textNode = this.renderer.createText(this.tooltipText);
    this.renderer.appendChild(this.tooltipElement, textNode);
    this.renderer.appendChild(document.body, this.tooltipElement);

    const resolvedPlacement = this.positionTooltip();

    // Create arrow caret
    const arrowElement = this.renderer.createElement('div');
    const arrowClasses = [
      'absolute',
      'w-1.5',
      'h-1.5',
      'bg-slate-900',
      'rotate-45',
      'pointer-events-none',
    ];
    arrowClasses.forEach((cls) => this.renderer.addClass(arrowElement, cls));

    switch (resolvedPlacement) {
      case 'top':
        this.renderer.addClass(arrowElement, 'bottom-[-3px]');
        this.renderer.addClass(arrowElement, 'left-1/2');
        this.renderer.addClass(arrowElement, '-translate-x-1/2');
        break;
      case 'bottom':
        this.renderer.addClass(arrowElement, 'top-[-3px]');
        this.renderer.addClass(arrowElement, 'left-1/2');
        this.renderer.addClass(arrowElement, '-translate-x-1/2');
        break;
      case 'left':
        this.renderer.addClass(arrowElement, 'right-[-3px]');
        this.renderer.addClass(arrowElement, 'top-1/2');
        this.renderer.addClass(arrowElement, '-translate-y-1/2');
        break;
      case 'right':
        this.renderer.addClass(arrowElement, 'left-[-3px]');
        this.renderer.addClass(arrowElement, 'top-1/2');
        this.renderer.addClass(arrowElement, '-translate-y-1/2');
        break;
    }
    this.renderer.appendChild(this.tooltipElement, arrowElement);

    requestAnimationFrame(() => {
      if (this.tooltipElement) {
        this.renderer.removeClass(this.tooltipElement, 'opacity-0');
        this.renderer.addClass(this.tooltipElement, 'opacity-100');
      }
    });
  }

  private positionTooltip(): 'top' | 'bottom' | 'left' | 'right' {
    if (!this.tooltipElement) {
      return 'top';
    }

    const hostRect = this.elementRef.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const offset = 8;

    const rawPlacements = Array.isArray(this.tooltipPlacement)
      ? this.tooltipPlacement
      : typeof this.tooltipPlacement === 'string'
        ? this.tooltipPlacement.split(/[\s,]+/)
        : ['top'];

    const resolvedPlacements: string[] = [];
    const hasAuto = rawPlacements.includes('auto');

    rawPlacements.forEach((p) => {
      const clean = p.trim().toLowerCase();
      if (clean && clean !== 'auto' && ['top', 'bottom', 'left', 'right'].includes(clean)) {
        resolvedPlacements.push(clean);
      }
    });

    if (hasAuto || resolvedPlacements.length === 0) {
      const fallbacks = ['top', 'bottom', 'left', 'right'];
      fallbacks.forEach((f) => {
        if (!resolvedPlacements.includes(f)) {
          resolvedPlacements.push(f);
        }
      });
    }

    let chosenPlacement = resolvedPlacements[0] as 'top' | 'bottom' | 'left' | 'right';
    for (const p of resolvedPlacements) {
      if (this.fits(p, hostRect, tooltipRect, viewportWidth, viewportHeight, offset)) {
        chosenPlacement = p as 'top' | 'bottom' | 'left' | 'right';
        break;
      }
    }

    let top = 0;
    let left = 0;

    switch (chosenPlacement) {
      case 'top':
        top = hostRect.top - tooltipRect.height - offset;
        left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = hostRect.bottom + offset;
        left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = hostRect.top + (hostRect.height - tooltipRect.height) / 2;
        left = hostRect.left - tooltipRect.width - offset;
        break;
      case 'right':
        top = hostRect.top + (hostRect.height - tooltipRect.height) / 2;
        left = hostRect.right + offset;
        break;
    }

    if (left < offset) {
      left = offset;
    } else if (left + tooltipRect.width > viewportWidth - offset) {
      left = viewportWidth - tooltipRect.width - offset;
    }

    if (top < offset) {
      top = offset;
    } else if (top + tooltipRect.height > viewportHeight - offset) {
      top = viewportHeight - tooltipRect.height - offset;
    }

    this.renderer.setStyle(this.tooltipElement, 'top', `${top + window.scrollY}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${left + window.scrollX}px`);

    return chosenPlacement;
  }

  private fits(
    placement: string,
    hostRect: DOMRect,
    tooltipRect: DOMRect,
    viewportWidth: number,
    viewportHeight: number,
    offset: number
  ): boolean {
    let top = 0;
    let left = 0;

    switch (placement) {
      case 'top':
        top = hostRect.top - tooltipRect.height - offset;
        left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = hostRect.bottom + offset;
        left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = hostRect.top + (hostRect.height - tooltipRect.height) / 2;
        left = hostRect.left - tooltipRect.width - offset;
        break;
      case 'right':
        top = hostRect.top + (hostRect.height - tooltipRect.height) / 2;
        left = hostRect.right + offset;
        break;
      default:
        return false;
    }

    return (
      top >= offset &&
      left >= offset &&
      top + tooltipRect.height <= viewportHeight - offset &&
      left + tooltipRect.width <= viewportWidth - offset
    );
  }

  private hideTooltip(): void {
    if (!this.tooltipElement) {
      return;
    }

    const element = this.tooltipElement;
    this.tooltipElement = null;

    this.renderer.removeClass(element, 'opacity-100');
    this.renderer.addClass(element, 'opacity-0');

    setTimeout(() => {
      if (element.parentNode) {
        this.renderer.removeChild(document.body, element);
      }
    }, 200);
  }
}
